// SPDX-License-Identifier: Elastic-2.0
/**
 * useNotificationPrefs — Zustand store for the user's Notifications
 * preferences (channels, categories, quiet hours).
 *
 * Spec refs:
 *  - Functional: specs/wip/feat-49-notifications-section/functional/notifications-section.md
 *    (§ 3 defaults, § 6 versioned shape, § 9 failure scenarios)
 *  - Technical:  specs/wip/feat-49-notifications-section/technical/notifications-section.md § 2
 *
 * Persistence: a single versioned JSON blob under
 * `localStorage["mirlai.notification_prefs"]`. Mirrors the read/write
 * pattern of `useLocale` / `useTheme` — every mutation persists
 * immediately and storage failures (quota exceeded, private mode,
 * disabled storage) are swallowed silently so the store still works
 * in-memory for the session.
 *
 * Forward-compat marker (F-208 handoff):
 * ------------------------------------------------------------------
 * Local-only at MVP (F-320). When F-208 ships the accounts notification-
 * preferences API, layer a sync wrapper:
 *   - On mount: fetch GET /me/preferences/notifications → merge (remote wins).
 *   - On each mutation: debounce PUT /me/preferences/notifications.
 *   - Local-only mode (mode !== "cloud") skips the network round-trip.
 * The persisted blob shape under § 2 IS the wire shape — F-208 should
 * accept it verbatim modulo the `version` field's role as a migration
 * cue.
 * ------------------------------------------------------------------
 */
import { create } from "zustand";

/** localStorage key the persisted blob is written under. */
export const PREFS_STORAGE_KEY = "mirlai.notification_prefs";

/**
 * Persisted blob version. Bump only when the on-disk shape changes
 * incompatibly. Older blobs are discarded + replaced with defaults
 * (see {@link loadPrefs}).
 */
export const PREFS_VERSION = 1;

export interface NotificationChannels {
  readonly push: boolean;
  readonly email: boolean;
  readonly in_app: boolean;
}

export interface NotificationCategories {
  readonly anomalies: boolean;
  readonly suggestions: boolean;
  readonly security: boolean;
}

export interface QuietHours {
  readonly enabled: boolean;
  /** 24h "HH:MM". */
  readonly start: string;
  /** 24h "HH:MM". */
  readonly end: string;
}

export interface NotificationPrefs {
  readonly version: typeof PREFS_VERSION;
  readonly channels: NotificationChannels;
  readonly categories: NotificationCategories;
  readonly quiet_hours: QuietHours;
}

/** Conservative defaults (see functional spec § 3). */
export const DEFAULT_PREFS: NotificationPrefs = {
  version: PREFS_VERSION,
  channels: { push: false, email: false, in_app: true },
  categories: { anomalies: true, suggestions: true, security: true },
  quiet_hours: { enabled: false, start: "22:00", end: "07:00" },
};

export interface NotificationPrefsState {
  prefs: NotificationPrefs;
  setChannel: (key: keyof NotificationChannels, value: boolean) => void;
  setCategory: (key: keyof NotificationCategories, value: boolean) => void;
  setQuietHours: (patch: Partial<QuietHours>) => void;
  reset: () => void;
}

/**
 * Type guard for a persisted blob. Strict on `version` so a future
 * v2 blob doesn't get silently coerced — discard + re-default is the
 * safe migration path until F-208 ships a proper upcast layer.
 */
function isNotificationPrefs(value: unknown): value is NotificationPrefs {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v.version === PREFS_VERSION &&
    typeof v.channels === "object" &&
    v.channels !== null &&
    typeof v.categories === "object" &&
    v.categories !== null &&
    typeof v.quiet_hours === "object" &&
    v.quiet_hours !== null
  );
}

/**
 * Read + validate the persisted blob. Returns `null` on absence,
 * parse failure, or version mismatch. Version mismatches emit a
 * `console.warn` so we have field telemetry on migration horizons.
 */
function readStored(): NotificationPrefs | null {
  try {
    const raw = globalThis.localStorage?.getItem(PREFS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isNotificationPrefs(parsed)) {
      console.warn("notification_prefs: discarding incompatible blob", parsed);
      return null;
    }
    return parsed;
  } catch {
    // JSON.parse threw on malformed blob, or localStorage access threw
    // (private mode etc.). Either way — fall back to defaults silently.
    return null;
  }
}

/** Public read-path: returns the stored blob if valid, else defaults. */
export function loadPrefs(): NotificationPrefs {
  return readStored() ?? DEFAULT_PREFS;
}

/** Write the full blob. Silent on quota / disabled storage failures. */
function persist(prefs: NotificationPrefs): void {
  try {
    globalThis.localStorage?.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Quota-exceeded / disabled storage → silent (matches useLocale).
  }
}

export const useNotificationPrefs = create<NotificationPrefsState>((set, get) => ({
  prefs: loadPrefs(),
  setChannel: (key, value) => {
    const current = get().prefs;
    const next: NotificationPrefs = {
      ...current,
      channels: { ...current.channels, [key]: value },
    };
    persist(next);
    set({ prefs: next });
  },
  setCategory: (key, value) => {
    const current = get().prefs;
    const next: NotificationPrefs = {
      ...current,
      categories: { ...current.categories, [key]: value },
    };
    persist(next);
    set({ prefs: next });
  },
  setQuietHours: (patch) => {
    const current = get().prefs;
    const merged: QuietHours = { ...current.quiet_hours, ...patch };
    // Auto-coerce: start === end is meaningless → fall back to disabled
    // (see functional spec § 5 + technical spec § 2.3).
    const normalised: QuietHours =
      merged.start === merged.end ? { ...merged, enabled: false } : merged;
    const next: NotificationPrefs = { ...current, quiet_hours: normalised };
    persist(next);
    set({ prefs: next });
  },
  reset: () => {
    persist(DEFAULT_PREFS);
    set({ prefs: DEFAULT_PREFS });
  },
}));
